import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import DynamicLockersTable from '../components/DynamicLockersTable';
import LockerConfigManager from '../components/LockerConfigManager';
import { ApiService } from '../utils/api';
import { ArrowLeft, LayoutGrid, Settings } from 'lucide-react';
import { useEventRoute } from '../utils/eventRef';

const EventLockers = () => {
  const { eventId, eventRef } = useEventRoute();
  const [activeTab, setActiveTab] = useState('dashboard');

  const { data: event, isLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => ApiService.getEvent(eventId),
    enabled: !!eventId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Event Not Found</h2>
        <Link to="/admin/events"><Button>Back to Events</Button></Link>
      </div>
    );
  }

  if (!event.lockerEnabled) {
    return (
      <div className="text-center py-8 space-y-3">
        <h2 className="text-xl font-semibold text-gray-900">Lockers are not enabled</h2>
        <p className="text-gray-600">Enable locker assignment for this event to manage lockers.</p>
        <Link to="/admin/events"><Button>Back to Events</Button></Link>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutGrid },
    { id: 'configure', label: 'Configure', icon: Settings },
  ];

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-4 lg:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Link to={`/admin/events/${eventRef}/registrations`}>
          <Button variant="outline" size="sm" className="w-fit">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        </Link>
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 break-words">{event.name}</h1>
          <p className="text-sm sm:text-base text-gray-600">
            Lockers{event.allowSharedLockers ? ` · shared (up to ${event.maxUsersPerLocker || 1}/locker)` : ''}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="p-0">
          <div className="flex items-center overflow-x-auto border-b border-gray-200">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === t.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <t.icon className="w-4 h-4" />
                <span>{t.label}</span>
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          {activeTab === 'dashboard' && <DynamicLockersTable eventId={eventId} />}
          {activeTab === 'configure' && <LockerConfigManager eventId={eventId} />}
        </CardContent>
      </Card>
    </div>
  );
};

export default EventLockers;
